import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-98 touch-target",
  {
    variants: {
      variant: {
        // Primary: Uses semantic primary token (bright in dark mode for visibility)
        default: "bg-primary !text-primary-foreground hover:bg-primary/90 hover:!text-primary-foreground shadow-md hover:shadow-primary [&_svg]:!text-primary-foreground",
        // Destructive: Red for dangerous actions
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md",
        // Outline: Border with hover fill - uses semantic tokens. Force foreground so
        // global dark-mode link/anchor rules cannot wash out button-styled anchors.
        outline: "border border-input bg-background !text-foreground hover:bg-primary hover:!text-primary-foreground hover:border-primary [&_svg]:!text-current",
        // Secondary: Light bg with dark text
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md",
        // Ghost: Subtle hover effect
        ghost: "hover:bg-muted hover:text-foreground",
        // Link: Text link style - uses primary which is bright in dark mode
        link: "text-primary underline-offset-4 hover:underline",
        // Tertiary: Dark text with light background on hover
        tertiary: "text-foreground bg-transparent hover:bg-muted",
        // Accent: Brand coral for highlights
        accent: "bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow",
        // Highlight: Achievement yellow
        highlight: "bg-highlight text-highlight-foreground hover:bg-highlight/90",
      },
      size: {
        default: "h-12 px-5 py-3 min-w-[48px] btn-label-md",
        sm: "h-10 px-4 py-2 min-w-[44px] btn-label-sm",
        lg: "h-14 px-8 py-4 min-w-[56px] btn-label-lg",
        icon: "h-12 w-12 min-w-[48px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    
    // When asChild is true, Slot requires exactly one child
    // We cannot add loading indicator siblings with asChild
    if (asChild) {
      return (
        <Comp 
          className={cn(buttonVariants({ variant, size, className }))} 
          ref={ref} 
          {...props}
        >
          {children}
        </Comp>
      );
    }
    
    return (
      <Comp 
        className={cn(buttonVariants({ variant, size, className }))} 
        ref={ref} 
        disabled={disabled || loading}
        aria-busy={loading}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {loading ? <span className="sr-only">Loading...</span> : null}
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
